import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { renderIcon } from '@/utils/iconMap';

export interface TabItem {
  id: string;
  label: string;
  icon?: string;
}

interface TabNavigationProps {
  tabs: TabItem[];
  defaultTab?: string;
  paramName?: 'tab' | 'view';
  className?: string;
  /**
   * When set, emits guided-tutorial anchors: `data-tour="{prefix}-bar"` on the
   * container and `data-tour="{prefix}-{tab.id}"` on each tab button.
   */
  dataTourPrefix?: string;
}

const TabNavigation: React.FC<TabNavigationProps> = ({
  tabs,
  defaultTab,
  paramName = 'tab',
  className = '',
  dataTourPrefix
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const activeTab = searchParams.get(paramName) || defaultTab || tabs[0]?.id;

  const handleTabChange = (tabId: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(paramName, tabId);
    setSearchParams(newParams);
  };

  useEffect(() => {
    if (!searchParams.get(paramName) && defaultTab) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set(paramName, defaultTab);
      setSearchParams(newParams, { replace: true });
    }
  }, []);

  return (
    <div className={`mb-8 transition-all duration-300 ${className}`}>
      <div className={`cgm-card rounded-2xl p-2 px-4`} data-tour={dataTourPrefix ? `${dataTourPrefix}-bar` : undefined}>
        <div className="flex w-full space-x-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              data-tour={dataTourPrefix ? `${dataTourPrefix}-${tab.id}` : undefined}
              onClick={() => handleTabChange(tab.id)}
              className={`cgm-tab flex items-center justify-center space-x-2 px-4 py-3 rounded-xl transition-all duration-200 flex-1 min-w-0 
                ${activeTab === tab.id ? 'cgm-tab-active' : ''}`}
            >
              {tab.icon && <span className="flex-shrink-0">{renderIcon(tab.icon, 'w-5 h-5')}</span>}
              <span className="hidden sm:block truncate">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TabNavigation;
